import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import formatDuration from '../../utils/formatDuration';
import type { TimeEntry } from '../../types/timeEntry';

function clock(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

// <input type="datetime-local"> 要的是本地时间字符串（无时区后缀），
// 而 toISOString() 给的是 UTC —— 直接用它会让补录的时间差 8 小时。
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// 只读两列 + 补录。不复用 tracker/Timer/TimeEntryList——那个是 antd 的，
// 硬复用会把 antd 拖回 /todo（spec §6.1）。
const EntryList: React.FC<{ taskId: number; onChanged: () => void }> = ({ taskId, onChanged }) => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const hourAgo = new Date(now.getTime() - 3600_000);
  const [startedAt, setStartedAt] = useState(toLocalInput(hourAgo));
  const [endedAt, setEndedAt] = useState(toLocalInput(now));

  const load = useCallback(() => {
    let cancelled = false;

    api.timeEntries
      .list({ taskId })
      .then((rows) => {
        if (!cancelled) setEntries(rows as TimeEntry[]);
      })
      .catch(() => {
        // 时间记录是附加信息，取不到就不显示这一段，不打断详情栏其余部分
        if (!cancelled) setEntries([]);
      });

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    setAdding(false);
    setError(null);
  }, [taskId]);

  const submit = async () => {
    setError(null);
    try {
      await api.timeEntries.create({
        task_id: taskId,
        started_at: new Date(startedAt).toISOString(),
        ended_at: new Date(endedAt).toISOString(),
      });
      setAdding(false);
      load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : '补录失败');
    }
  };

  const remove = async (id: number) => {
    setError(null);
    try {
      await api.timeEntries.destroy(id);
      load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : '删除失败');
    }
  };

  const finished = entries.filter((entry) => entry.ended_at);
  const totalMinutes = Math.round(
    finished.reduce((sum, entry) => sum + (entry.duration_seconds ?? 0), 0) / 60
  );

  return (
    <div className="mb-[9px] rounded-[9px] border border-todo-line bg-todo-card shadow-sm">
      <div className="px-[13px] pb-[11px] pt-[9px]">
        <h4 className="mb-1.5 flex items-center text-[11px] font-medium tracking-wide text-todo-muted">
          <span className="flex-1">
            时间记录{finished.length > 0 && ` · 共 ${formatDuration(totalMinutes)}`}
          </span>
          <button
            type="button"
            onClick={() => setAdding((prev) => !prev)}
            className="text-todo-accent hover:underline"
          >
            {adding ? '取消' : '＋ 补录'}
          </button>
        </h4>

        {finished.length === 0 && !adding && (
          <div className="py-1 text-[12px] text-todo-muted">还没有计时记录。</div>
        )}

        {finished.map((entry) => (
          <div
            key={entry.id}
            className="group flex items-center gap-2 py-[2.5px] text-[12px] tabular-nums text-todo-ink2"
          >
            <span className="flex-1">
              {clock(entry.started_at)} – {clock(entry.ended_at as string)}
              {entry.source === 'manual' && (
                <span className="ml-1.5 not-italic text-todo-muted">补录</span>
              )}
            </span>
            <span className="text-todo-muted">
              {formatDuration(Math.round((entry.duration_seconds ?? 0) / 60))}
            </span>
            <button
              type="button"
              aria-label="删除这条计时记录"
              onClick={() => remove(entry.id)}
              className="px-1 text-todo-line2 opacity-0 transition-opacity hover:text-todo-urgent focus:opacity-100 group-hover:opacity-100"
            >
              ✕
            </button>
          </div>
        ))}

        {adding && (
          <div className="mt-2 space-y-1.5 border-t border-todo-line pt-2">
            <label className="flex items-center gap-2 text-[12px] text-todo-ink2">
              <span className="w-8 flex-none text-todo-muted">开始</span>
              <input
                type="datetime-local"
                value={startedAt}
                onChange={(event) => setStartedAt(event.target.value)}
                className="flex-1 rounded border border-todo-line bg-todo-card px-1.5 py-0.5 tabular-nums outline-none"
              />
            </label>
            <label className="flex items-center gap-2 text-[12px] text-todo-ink2">
              <span className="w-8 flex-none text-todo-muted">结束</span>
              <input
                type="datetime-local"
                value={endedAt}
                onChange={(event) => setEndedAt(event.target.value)}
                className="flex-1 rounded border border-todo-line bg-todo-card px-1.5 py-0.5 tabular-nums outline-none"
              />
            </label>
            <button
              type="button"
              onClick={submit}
              className="w-full rounded-md bg-todo-accent px-2 py-1 text-[12px] text-white"
            >
              补录这段时间
            </button>
          </div>
        )}

        {error && <div className="mt-1.5 text-[12px] text-todo-urgent">{error}</div>}
      </div>
    </div>
  );
};

export default EntryList;
